<template>
  <div 
    class="themed-table-container"
    :class="{ 'loading': isLoading }"
  >
    <!-- ARIA live region for status announcements -->
    <div 
      class="sr-only" 
      role="status" 
      aria-live="polite" 
      aria-atomic="true"
    >
      <span v-if="isLoading">Loading table data</span>
      <span v-else-if="!hasData">No data available</span>
      <span v-else-if="hasPagination">
        Showing {{ (currentPage - 1) * currentPerPage + 1 }} to {{ Math.min(currentPage * currentPerPage, totalItems) }} of {{ totalItems }} items
      </span>
      <span v-else>
        Showing {{ totalItems }} {{ totalItems === 1 ? 'item' : 'items' }}
      </span>
    </div>
    
    <!-- ARIA live region for selection changes -->
    <div 
      v-if="hasBulkActions"
      class="sr-only" 
      role="status" 
      aria-live="polite" 
      aria-atomic="true"
    >
      <span v-if="selectedRows.length > 0">
        {{ selectedRows.length }} {{ selectedRows.length === 1 ? 'row' : 'rows' }} selected
      </span>
    </div>
    
    <div 
      class="themed-table-wrapper"
      role="region"
      :aria-label="ariaLabel || 'Data table'"
      tabindex="0"
    >
      <table 
        :class="tableClasses"
        v-bind="$attrs"
        role="table"
        :aria-busy="isLoading ? 'true' : 'false'"
        :aria-label="ariaLabel"
        :aria-describedby="ariaDescribedBy"
      >
        <!-- Auto-rendered header using TableHeader component -->
        <table-header
          v-if="useAutoRendering"
          :columns="visibleColumns"
          :sort-column="currentSortColumn"
          :sort-direction="currentSortDirection"
          :selectable="hasBulkActions"
          :all-selected="allRowsSelected"
          :disabled="isLoading"
          :column-widths="columnWidths"
          @sort="handleSort"
          @select-all="handleSelectAll"
          @resize-start="handleResizeStart"
        />
        
        <!-- Slot-based header (takes precedence) -->
        <thead v-else-if="hasHeaderSlot" class="no-bg" role="rowgroup">
          <slot name="header"></slot>
        </thead>
        
        <!-- Auto-rendered body using TableCell component -->
        <tbody v-if="useAutoRendering && !isLoading && hasData" role="rowgroup">
          <tr
            v-for="(row, rowIndex) in paginatedData"
            :key="getRowKey(row, rowIndex)"
            role="row"
            :class="{ 'themed-table-row-selected': isRowSelected(row) }"
            @click="handleRowClick(row)"
          >
            <!-- Selection checkbox -->
            <td 
              v-if="hasBulkActions"
              class="themed-table-cell themed-table-cell-checkbox"
              role="cell"
            >
              <input
                type="checkbox"
                :checked="isRowSelected(row)"
                @change="handleRowSelect(row, $event.target.checked)"
                @keydown.space.prevent="handleRowSelectKeydown(row, $event)"
                @click.stop
                :aria-label="`Select row ${rowIndex + 1}`"
                :disabled="isLoading"
                tabindex="0"
              />
            </td>
            
            <!-- Data cells -->
            <table-cell
              v-for="(column, colIndex) in visibleColumns"
              :key="column.field || colIndex"
              :column="column"
              :row="row"
              :row-index="rowIndex"
              :value="getCellValue(row, column.field)"
              :editable="inlineEditable && column.editable && isInEditState"
              :editing="getEditingState(rowIndex, column.field)"
              :edit-value="getEditValue(rowIndex, column.field)"
              :has-column-slot="hasColumnSlot(column.field)"
              :has-actions-slot="hasActionsSlot"
              :has-editable-columns="hasInlineEditing"
              :saving="getSavingState(rowIndex)"
              :column-width="columnWidths[column.field]"
              :show-row-details="shouldShowInfoIcon"
              :edit-mode="editMode"
              @edit="handleEdit"
              @update="handleUpdate"
              @cancel="handleCancelEdit"
              @action="handleAction"
              @start-edit-row="startEditRow(rowIndex)"
              @start-edit-cell="startEditCell(rowIndex, column.field)"
              @save-edit-row="saveEditRow(rowIndex)"
              @cancel-edit-row="cancelEditRow(rowIndex)"
              @save-edit-cell="saveEditCell(rowIndex, column.field)"
              @cancel-edit-cell="cancelEditCell"
              @update-edit-value="updateEditValue"
              @show-details="showRowDetailsModal(row)"
            >
              <!-- Pass through column-specific slot -->
              <template 
                v-if="hasColumnSlot(column.field)"
                v-slot:[`column-${column.field}`]
              >
                <slot 
                  :name="`column-${column.field}`"
                  :row="row"
                  :value="getCellValue(row, column.field)"
                  :column="column"
                ></slot>
              </template>
              
              <!-- Pass through actions slot -->
              <template 
                v-if="column.type === 'actions' && hasActionsSlot"
                v-slot:actions
              >
                <slot 
                  name="actions"
                  :row="row"
                ></slot>
              </template>
            </table-cell>
          </tr>
        </tbody>
        
        <!-- Loading and Empty States -->
        <tbody v-if="useAutoRendering && (isLoading || !hasData)" role="rowgroup">
          <tr role="row">
            <td 
              :colspan="totalColumns" 
              class="themed-table-state-cell"
              role="cell"
            >
              <table-empty
                :loading="isLoading"
                :is-empty="!hasData && !isLoading"
              >
                <template v-if="$slots['loading']" #loading>
                  <slot name="loading"></slot>
                </template>
                <template v-if="$slots['empty-state']" #empty-state>
                  <slot name="empty-state"></slot>
                </template>
              </table-empty>
            </td>
          </tr>
        </tbody>
        
        <!-- Slot-based body (takes precedence) -->
        <tbody v-else role="rowgroup">
          <slot></slot>
        </tbody>
        
        <!-- Table Footer -->
        <tfoot v-if="hasFooterSlot" role="rowgroup">
          <slot name="footer"></slot>
        </tfoot>
      </table>
    </div>
    
    <!-- Pagination Controls -->
    <themed-pagination
      v-if="showPagination"
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-items="totalItems"
      :per-page="currentPerPage"
      @first="handleFirstPage"
      @previous="handlePreviousPage"
      @next="handleNextPage"
      @last="handleLastPage"
    />
    
    <!-- Row Details Modal -->
    <row-details-popup
      modal-name="row-details-modal"
      :title="rowDetailsTitle"
      :row="selectedRowForDetails"
      :columns="detailsColumns"
      :actions="actionButtons"
      @closed="handleModalClosed"
      @action="handleActionFromPopup"
    >
      <!-- Pass through only slots that actually exist from parent -->
      <template
        v-for="column in detailsColumnsWithSlots"
        v-slot:[`column-${column.field}`]="slotProps"
      >
        <slot
          :name="`column-${column.field}`"
          :row="slotProps.row"
          :value="slotProps.value"
          :column="slotProps.column"
        ></slot>
      </template>
    </row-details-popup>
  </div>
</template>

<script>
import TableHeader from './table/TableHeader.vue';
import TableCell from './table/TableCell.vue';
import ThemedPagination from './ThemedPagination.vue';
import TableEmpty from './table/TableEmpty.vue';
import RowDetailsPopup from './table/RowDetailsPopup.vue';

export default {
  name: 'ThemedTable',
  components: {
    TableHeader,
    TableCell,
    ThemedPagination,
    TableEmpty,
    RowDetailsPopup
  },
  props: {
    // Data source (provide one)
    data: {
      type: Array,
      default: null
    },
    apiEndpoint: {
      type: String,
      default: null
    },
    
    // Accessibility
    ariaLabel: {
      type: String,
      default: null
    },
    ariaDescribedBy: {
      type: String,
      default: null
    },
    
    // Column configuration
    columns: {
      type: Array,
      default: null
      // Format: [{ field, label, type, sortable, searchable, editable, width, formatter, options, validation }]
    },
    
    // API endpoints for updates
    updateEndpoint: {
      type: String,
      default: null
      // Format: '/api/resource/:id' - :id will be replaced with row.id
      // Used for inline editing
    },
    
    // Bulk update endpoint
    bulkUpdateEndpoint: {
      type: String,
      default: null
      // Format: '/api/resource/bulk'
      // Used for bulk actions - sends array of items to update
    },
    
    // Bulk update payload key
    bulkUpdatePayloadKey: {
      type: String,
      default: 'items'
      // Key name for the array in bulk update payload
      // Example: 'categories', 'variables', 'items'
    },
    
    // Row identifier field
    rowIdField: {
      type: String,
      default: 'id'
      // Field name to use as unique identifier for rows
    },
    
    // Features
    searchable: {
      type: Boolean,
      default: false
    },
    sortable: {
      type: Boolean,
      default: false
    },
    inlineEditable: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: String,
      default: 'row',
      validator: (v) => ['row', 'cell', 'toggle', 'bulk'].includes(v)
      // 'row' - Click cell to edit entire row (default)
      // 'cell' - Click cell to edit only that cell
      // 'toggle' - External button toggles edit mode, each row has Save/Cancel
      // 'bulk' - All rows editable, external Save All button
    },
    externalEditMode: {
      type: Boolean,
      default: false
      // Controls edit state when editMode is 'toggle' or 'bulk'
    },
    showRowDetails: {
      type: Boolean,
      default: false
      // Show info icon to display all row details (including hidden columns)
    },
    rowDetailsTitle: {
      type: String,
      default: 'Row Details'
      // Title for the row details modal
    },
    
    // Pagination
    pagination: {
      type: [Boolean, Object],
      default: false
      // Pass true to enable pagination with defaults (25 per page)
      // Or pass object: { current, total, perPage, totalItems } for custom config
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100]
    },
    
    // Bulk actions
    bulkActions: {
      type: Array,
      default: null
      // Format: [{ name, label, icon, variant, field, value }]
    },
    
    // Styling
    striped: {
      type: Boolean,
      default: false
    },
    hoverable: {
      type: Boolean,
      default: true
    },
    bordered: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v)
    },
    
    // States
    loading: {
      type: Boolean,
      default: false
    },
    
    // API options
    autoUpdate: {
      type: Boolean,
      default: false
    },
    
    // Column visibility
    columnVisibility: {
      type: Boolean,
      default: false
      // Enable column visibility toggle feature
    },
    defaultVisibleColumns: {
      type: Array,
      default: null
      // Array of field names that should be visible by default
      // If not provided, all columns with showInTable !== false will be visible
    }
  },
  data() {
    return {
      currentSortColumn: null,
      currentSortDirection: 'asc',
      currentSearchQuery: '',
      selectedRows: [],
      processingBulkAction: false,
      internalData: [],
      internalPage: 1,
      internalPerPage: null,
      apiData: [],
      apiPagination: null,
      internalLoading: false,
      apiError: null,
      
      // Inline editing state
      editingRowIndex: null,
      editingRowForm: {},
      originalRowData: {},
      savingRow: false,
      editingCell: null, // For cell mode: { rowIndex, field }
      
      // Bulk edit state
      bulkEditForms: {}, // Map of rowIndex -> form data
      originalBulkData: {}, // Map of rowIndex -> original data
      savingBulkEdit: false,
      
      // Row details modal state
      selectedRowForDetails: null,
      
      // Column resizing state
      columnWidths: {},
      resizingColumn: null,
      resizeStartX: 0,
      resizeStartWidth: 0,
      
      // Column visibility state
      internalVisibleColumns: []
    };
  },
  computed: {
    displayData() {
      // Return API data in API mode, otherwise local data
      if (this.isApiMode) {
        return this.apiData;
      }
      
      // In local mode, apply search filtering and sorting
      let data = this.data || this.internalData || [];
      
      // Apply search filter if search is enabled and query exists
      if (this.hasSearch && this.currentSearchQuery) {
        data = this.filterData(data, this.currentSearchQuery);
      }
      
      // Apply sorting if a sort column is selected
      if (this.currentSortColumn) {
        data = this.sortData(data, this.currentSortColumn, this.currentSortDirection);
      }
      
      return data;
    },
    
    paginatedData() {
      // In API mode, pagination is handled by the server
      if (this.isApiMode) {
        return this.displayData;
      }
      
      // In local mode with pagination, apply client-side pagination
      if (this.hasPagination && this.isLocalMode) {
        const start = (this.currentPage - 1) * this.currentPerPage;
        const end = start + this.currentPerPage;
        return this.displayData.slice(start, end);
      }
      
      // No pagination, return all data
      return this.displayData;
    },
    
    currentPage() {
      // Use pagination prop if it's an object with current page
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.current) {
        return this.pagination.current;
      }
      
      // Otherwise use internal page state
      return this.internalPage;
    },
    
    currentPerPage() {
      // User selection takes priority
      if (this.internalPerPage !== null) {
        return this.internalPerPage;
      }
      
      // Use pagination prop if it's an object with perPage
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.perPage) {
        return this.pagination.perPage;
      }
      
      // Default to 25
      return 25;
    },
    
    totalItems() {
      // In API mode, use API pagination data
      if (this.isApiMode && this.apiPagination) {
        return this.apiPagination.totalItems;
      }
      
      // Use pagination prop if it's an object with totalItems
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.totalItems !== undefined) {
        return this.pagination.totalItems;
      }
      
      // Count all items from displayData
      return this.displayData.length;
    },
    
    totalPages() {
      // In API mode, use API pagination data
      if (this.isApiMode && this.apiPagination) {
        return this.apiPagination.total;
      }
      
      // Use pagination prop if it's an object with total
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.total !== undefined) {
        return this.pagination.total;
      }
      
      // Calculate total pages (minimum 1 to satisfy TablePagination validator)
      const calculated = Math.ceil(this.totalItems / this.currentPerPage);
      return Math.max(1, calculated);
    },
    
    isLoading() {
      return this.loading || this.internalLoading;
    },
    
    totalColumns() {
      let count = this.columns ? this.columns.length : 0;
      if (this.hasBulkActions) {
        count += 1; // Add checkbox column
      }
      return count;
    },
    
    allRowsSelected() {
      if (!this.paginatedData || this.paginatedData.length === 0) {
        return false;
      }
      return this.selectedRows.length === this.paginatedData.length;
    },
    
    tableClasses() {
      const classes = ['themed-table'];
      
      if (this.compact) {
        classes.push('themed-table-compact');
      }
      
      if (this.bordered) {
        classes.push('themed-table-bordered');
      }
      
      if (this.striped) {
        classes.push('themed-table-striped');
      }
      
      if (this.hoverable) {
        classes.push('themed-table-hoverable');
      }
      
      if (this.size) {
        classes.push(`themed-table-${this.size}`);
      }
      
      return classes.join(' ');
    },
    
    // Feature detection
    isApiMode() {
      return !!this.apiEndpoint;
    },
    isLocalMode() {
      return this.data !== null && !this.apiEndpoint;
    },
    hasColumns() {
      return !!this.columns && this.columns.length > 0;
    },
    
    // Slot detection logic
    hasHeaderSlot() {
      return !!this.$slots.header;
    },
    hasDefaultSlot() {
      return !!this.$slots.default;
    },
    hasFooterSlot() {
      return !!this.$slots.footer;
    },
    hasSlots() {
      return this.hasHeaderSlot || this.hasDefaultSlot;
    },
    
    // Slot precedence logic - slots take priority over auto-rendering
    useAutoRendering() {
      return this.hasColumns && !this.hasSlots;
    },
    
    hasPagination() {
      // Show pagination if pagination prop is true/object OR if API returned pagination data
      return !!this.pagination || (this.isApiMode && !!this.apiPagination);
    },
    
    showPagination() {
      // Only show pagination if enabled AND there's more than one page
      return this.hasPagination && this.totalPages > 1;
    },
    
    hasPerPageSelector() {
      // Show per-page selector when pagination is enabled
      return this.hasPagination;
    },
    hasSearch() {
      return this.searchable;
    },
    hasBulkActions() {
      return !!this.bulkActions && this.bulkActions.length > 0;
    },
    hasInlineEditing() {
      return this.inlineEditable && this.columns?.some(c => c.editable);
    },
    isToggleMode() {
      return this.editMode === 'toggle';
    },
    isBulkMode() {
      return this.editMode === 'bulk';
    },
    isCellMode() {
      return this.editMode === 'cell';
    },
    isRowMode() {
      return this.editMode === 'row';
    },
    isInEditState() {
      // For toggle/bulk modes, check externalEditMode prop
      if (this.isToggleMode || this.isBulkMode) {
        return this.externalEditMode;
      }
      // For row/cell modes, always allow editing
      return true;
    },
    hasData() {
      return this.paginatedData && this.paginatedData.length > 0;
    },
    hasActionsSlot() {
      return !!this.$slots.actions || !!this.$scopedSlots.actions;
    },
    hasActiveFilters() {
      // Check if there are any active filters or sorting
      return !!(this.currentSearchQuery || this.currentSortColumn);
    },
    hasColumnVisibility() {
      return this.columnVisibility && this.hasColumns;
    },
    
    // Inline editing computed properties
    editableColumns() {
      return this.columns?.filter(c => c.editable) || [];
    },
    
    hasUpdateEndpoint() {
      return !!this.updateEndpoint;
    },
    
    // Row details computed properties
    visibleColumns() {
      // If column visibility feature is disabled, return columns as-is
      // (parent component handles filtering)
      if (!this.columnVisibility) {
        return this.columns || [];
      }
      
      // With column visibility enabled, filter based on user selection
      const allColumns = this.columns || [];
      const visibleFields = this.currentVisibleColumns;
      
      return allColumns.filter(col => {
        // Always show action columns
        if (col.type === 'actions') {
          return true;
        }
        
        // Check if column is in visible list
        return visibleFields.includes(col.field);
      });
    },
    
    currentVisibleColumns() {
      // Return the current list of visible column fields
      if (this.internalVisibleColumns.length > 0) {
        return this.internalVisibleColumns;
      }
      
      // Initialize from defaultVisibleColumns prop if provided
      if (this.defaultVisibleColumns && this.defaultVisibleColumns.length > 0) {
        return this.defaultVisibleColumns;
      }
      
      // Default: all columns with showInTable !== false
      return this.columns
        ?.filter(col => col.showInTable !== false && col.type !== 'actions')
        .map(col => col.field) || [];
    },
    
    hiddenColumns() {
      // Columns that are hidden from table but shown in details modal
      return this.columns?.filter(col => col.showInTable === false) || [];
    },
    
    detailsColumns() {
      // All columns except actions for the details modal
      return this.columns?.filter(col => col.type !== 'actions') || [];
    },
    
    detailsColumnsWithSlots() {
      // Only columns that have slots defined in parent component
      return this.detailsColumns.filter(col => this.hasColumnSlot(col.field));
    },
    
    actionColumns() {
      // Get action columns for modal footer
      return this.columns?.filter(col => col.type === 'actions') || [];
    },
    
    actionButtons() {
      // Extract all buttons from action columns for RowDetailsPopup
      const buttons = [];
      
      for (const column of this.actionColumns) {
        if (column.buttons && Array.isArray(column.buttons)) {
          buttons.push(...column.buttons);
        }
      }
      
      return buttons;
    },
    
    hasHiddenColumns() {
      return this.hiddenColumns.length > 0;
    },
    
    shouldShowInfoIcon() {
      // Show info icon only if explicitly enabled via showRowDetails prop
      return this.showRowDetails === true;
    }
  },
  methods: {
    hasColumnSlot(field) {
      const slotName = `column-${field}`;
      return !!this.$slots[slotName] || !!this.$scopedSlots[slotName];
    },
    
    getRowKey(row, index) {
      // Use rowIdField prop to get the correct identifier
      const idValue = row[this.rowIdField];
      return idValue !== undefined && idValue !== null ? idValue : index;
    },
    
    getCellValue(row, field) {
      if (!field) {
        return null;
      }
      
      // Support nested field access with dot notation
      const fields = field.split('.');
      let value = row;
      
      for (const f of fields) {
        if (value && typeof value === 'object') {
          value = value[f];
        } else {
          return null;
        }
      }
      
      return value;
    },
    
    handleSort(field) {
      // Cycle through sort states: asc → desc → none (reset)
      if (this.currentSortColumn === field) {
        if (this.currentSortDirection === 'asc') {
          // First click was asc, now go to desc
          this.currentSortDirection = 'desc';
        } else if (this.currentSortDirection === 'desc') {
          // Second click was desc, now reset (no sorting)
          this.currentSortColumn = null;
          this.currentSortDirection = 'asc';
        }
      } else {
        // Different column, start with asc
        this.currentSortColumn = field;
        this.currentSortDirection = 'asc';
      }
      
      // In API mode, fetch new data with sort parameters
      if (this.isApiMode) {
        this.fetchApiData();
      }
      
      // Emit sort event for parent to be aware
      this.$emit('sort-change', {
        column: this.currentSortColumn,
        direction: this.currentSortDirection
      });
    },
    
    handleRowClick(row) {
      if (this.isLoading) {
        return;
      }
      this.$emit('row-click', { row });
    },
    
    isRowSelected(row) {
      const rowKey = this.getRowKey(row, -1);
      return this.selectedRows.some(selected => this.getRowKey(selected, -1) === rowKey);
    },
    
    handleRowSelect(row, checked) {
      if (this.isLoading) {
        return;
      }
      
      this.toggleRowSelection(row);
    },
    
    handleSelectAll(checked) {
      if (this.isLoading) {
        return;
      }
      
      this.toggleAllRows();
    },
    
    handleRowSelectKeydown(row, event) {
      // Toggle checkbox on space key
      const checkbox = event.target;
      checkbox.checked = !checkbox.checked;
      this.handleRowSelect(row, checkbox.checked);
    },
    
    handlePageChange(page) {
      if (this.isLoading) {
        return;
      }
      
      // Validate page number
      if (page < 1 || page > this.totalPages) {
        return;
      }
      
      // In API mode, update internal page and fetch new data
      if (this.isApiMode) {
        this.internalPage = page;
        this.fetchApiData();
        this.$emit('page-change', { page });
        return;
      }
      
      // In local mode, update internal page state
      this.internalPage = page;
      
      // Emit event for parent to be aware of page change
      this.$emit('page-change', { page });
    },
    
    handleFirstPage() {
      this.handlePageChange(1);
    },
    
    handlePreviousPage() {
      if (this.currentPage > 1) {
        this.handlePageChange(this.currentPage - 1);
      }
    },
    
    handleNextPage() {
      if (this.currentPage < this.totalPages) {
        this.handlePageChange(this.currentPage + 1);
      }
    },
    
    handleLastPage() {
      this.handlePageChange(this.totalPages);
    },
    
    handlePerPageChange(perPage) {
      if (this.isLoading) {
        return;
      }
      
      // Update internal state
      this.internalPerPage = perPage;
      
      // Reset to first page when changing per-page
      this.internalPage = 1;
      
      // In API mode, fetch new data
      if (this.isApiMode) {
        this.fetchApiData();
      }
      
      // Emit event for parent to be aware of per-page change
      this.$emit('per-page-change', { perPage });
    },
    
    isEditing(row, field) {
      if (!this.editingCell) {
        return false;
      }
      
      const rowKey = this.getRowKey(row, -1);
      return this.editingCell.rowKey === rowKey && this.editingCell.field === field;
    },
    
    handleEdit(row, field) {
      const rowKey = this.getRowKey(row, -1);
      this.editingCell = { rowKey, field };
    },
    
    async handleUpdate(row, field, value) {
      const oldValue = this.getCellValue(row, field);
      
      // If API auto-update is enabled and we have an API endpoint
      if (this.autoUpdate && this.apiEndpoint && row.id) {
        try {
          // Send PATCH request to update the row
          const response = await window.axios.patch(`${this.apiEndpoint}/${row.id}`, {
            [field]: value
          });
          
          // Update the row data with the response
          if (response.data) {
            // Find and update the row in apiData
            const rowIndex = this.apiData.findIndex(r => r.id === row.id);
            if (rowIndex !== -1) {
              this.$set(this.apiData, rowIndex, response.data);
            }
          }
          
          // Emit update event with success
          this.$emit('update', {
            row,
            field,
            value,
            oldValue,
            success: true
          });
          
          // In API mode, refresh data to get latest state from server
          if (this.isApiMode) {
            await this.fetchApiData();
          }
        } catch (error) {
          // Emit error event
          this.$emit('error', {
            error,
            context: 'inline-edit',
            row,
            field,
            value
          });
          
          // Emit update event with failure
          this.$emit('update', {
            row,
            field,
            value,
            oldValue,
            success: false,
            error
          });
          
          // Don't clear editing state on error so user can retry
          return;
        }
      } else {
        // Local mode - just emit the event
        this.$emit('update', {
          row,
          field,
          value,
          oldValue
        });
      }
      
      // Clear editing state
      this.editingCell = null;
    },
    
    handleCancelEdit() {
      // Clear editing state
      this.editingCell = null;
    },
    
    toggleRowSelection(row) {
      const rowKey = this.getRowKey(row, -1);
      const isSelected = this.isRowSelected(row);
      
      if (isSelected) {
        // Remove from selection
        this.selectedRows = this.selectedRows.filter(
          selected => this.getRowKey(selected, -1) !== rowKey
        );
      } else {
        // Add to selection
        this.selectedRows.push(row);
      }
      
      this.$emit('selection-change', { selected: this.selectedRows });
    },
    
    toggleAllRows() {
      if (this.allRowsSelected) {
        // Deselect all
        this.selectedRows = [];
      } else {
        // Select all visible rows
        this.selectedRows = [...this.paginatedData];
      }
      
      this.$emit('selection-change', { selected: this.selectedRows });
    },
    
    async handleBulkAction(actionName) {
      if (this.isLoading || this.processingBulkAction) {
        return;
      }
      
      // Find the bulk action configuration
      const action = this.bulkActions?.find(a => a.name === actionName);
      
      if (!action) {
        console.warn(`Bulk action "${actionName}" not found in configuration`);
        return;
      }
      
      // Emit bulk-action-start event
      this.$emit('bulk-action-start', {
        action: actionName,
        selected: this.selectedRows
      });
      
      // Check if action has field and value properties for automatic processing
      if (action.field !== undefined && action.value !== undefined) {
        // Automatic bulk action processing
        await this.processAutomaticBulkAction(action);
      } else {
        // Custom bulk action - emit event for parent to handle
        this.$emit('bulk-action-complete', {
          action: actionName,
          selected: this.selectedRows
        });
        
        // Clear selection after successful bulk action
        this.selectedRows = [];
      }
    },
    
    async processAutomaticBulkAction(action) {
      // Check if bulkUpdateEndpoint is provided (preferred for bulk operations)
      if (this.bulkUpdateEndpoint) {
        await this.processBulkUpdateEndpoint(action);
        return;
      }
      
      // Check if updateEndpoint is provided (fallback to individual updates)
      if (!this.hasUpdateEndpoint) {
        // No endpoint - emit event for parent to handle
        this.$emit('bulk-action-complete', {
          action: action.name,
          selected: this.selectedRows,
          field: action.field,
          value: action.value
        });
        
        // Clear selection
        this.selectedRows = [];
        return;
      }
      
      // Set processing state
      this.processingBulkAction = true;
      
      const results = [];
      const errors = [];
      
      try {
        // Process each selected row individually
        for (const row of this.selectedRows) {
          try {
            const rowId = this.getRowId(row);
            const url = this.buildUpdateUrl(rowId);
            
            // Build complete payload with all row fields plus the update
            const payload = this.buildUpdatePayload(row, {
              [action.field]: action.value
            });
            
            // Make PUT/PATCH request
            const response = await window.axios.put(url, payload);
            
            // Update local data on success
            if (response.data) {
              const dataSource = this.isApiMode ? this.apiData : (this.data || this.internalData);
              const rowIndex = dataSource.findIndex(r => this.getRowId(r) === rowId);
              
              if (rowIndex !== -1) {
                this.$set(dataSource, rowIndex, response.data);
              }
            }
            
            results.push({
              row,
              success: true,
              data: response.data
            });
            
          } catch (error) {
            errors.push({
              row,
              error
            });
          }
        }
        
        // Check if all succeeded or some failed
        if (errors.length === 0) {
          // All succeeded
          this.$emit('bulk-action-complete', {
            action: action.name,
            selected: this.selectedRows,
            results
          });
          
          // Clear selection after successful bulk action
          this.selectedRows = [];
          
          // In API mode, refresh data to get latest state from server
          if (this.isApiMode) {
            await this.fetchApiData();
          }
        } else {
          // Some or all failed
          this.$emit('bulk-action-error', {
            action: action.name,
            selected: this.selectedRows,
            errors,
            results
          });
          
          // Don't clear selection on error so user can retry
        }
        
      } finally {
        // Clear processing state
        this.processingBulkAction = false;
      }
    },
    
    handleSearchChange(query) {
      if (this.isLoading) {
        return;
      }
      
      // Update internal search query
      this.currentSearchQuery = query;
      
      // Reset to first page when search changes
      this.internalPage = 1;
      
      // In API mode, fetch new data with search parameter
      if (this.isApiMode) {
        this.fetchApiData();
      }
      
      // Emit event for parent to be aware
      this.$emit('search-change', { query });
    },
    
    filterData(data, query) {
      // If no query, return all data
      if (!query || query.trim() === '') {
        return data;
      }
      
      // Convert query to lowercase for case-insensitive search
      const searchTerm = query.toLowerCase().trim();
      
      // Get searchable columns
      const searchableColumns = this.columns
        ? this.columns.filter(col => col.searchable !== false)
        : [];
      
      // If no columns defined or no searchable columns, search all fields
      if (!this.columns || searchableColumns.length === 0) {
        return data.filter(row => {
          return Object.values(row).some(value => {
            if (value === null || value === undefined) {
              return false;
            }
            return String(value).toLowerCase().includes(searchTerm);
          });
        });
      }
      
      // Filter data based on searchable columns
      return data.filter(row => {
        return searchableColumns.some(column => {
          const value = this.getCellValue(row, column.field);
          
          if (value === null || value === undefined) {
            return false;
          }
          
          // Convert value to string and search
          return String(value).toLowerCase().includes(searchTerm);
        });
      });
    },
    
    sortData(data, field, direction) {
      // Create a copy of the data array to avoid mutating the original
      const sortedData = [...data];
      
      // Sort the data based on the field and direction
      sortedData.sort((a, b) => {
        const aValue = this.getCellValue(a, field);
        const bValue = this.getCellValue(b, field);
        
        // Handle null/undefined values - push them to the end
        if (aValue === null || aValue === undefined) {
          return 1;
        }
        if (bValue === null || bValue === undefined) {
          return -1;
        }
        
        // Determine the comparison result based on value types
        let comparison = 0;
        
        // Check if values are numbers
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          // Numeric comparison
          comparison = aNum - bNum;
        } else if (aValue instanceof Date && bValue instanceof Date) {
          // Date comparison
          comparison = aValue.getTime() - bValue.getTime();
        } else {
          // String comparison (case-insensitive)
          const aStr = String(aValue).toLowerCase();
          const bStr = String(bValue).toLowerCase();
          comparison = aStr.localeCompare(bStr);
        }
        
        // Apply sort direction
        return direction === 'asc' ? comparison : -comparison;
      });
      
      return sortedData;
    },
    
    handleAction(actionName, row) {
      // Emit action event with action name and row data
      this.$emit('action', {
        action: actionName,
        row
      });
    },
    
    async fetchApiData() {
      // Only fetch if in API mode
      if (!this.isApiMode) {
        return;
      }
      
      // Set loading state
      this.internalLoading = true;
      this.apiError = null;
      
      try {
        // Build query parameters
        const params = this.buildApiParams();
        
        // Make GET request to API endpoint
        const response = await window.axios.get(this.apiEndpoint, { params });
        
        // Parse Laravel pagination format
        this.parseApiResponse(response.data);
        
      } catch (error) {
        // Handle error
        this.apiError = error;
        
        // Emit error event
        this.$emit('error', {
          error,
          context: 'api-fetch'
        });
        
        console.error('ThemedTable API fetch error:', error);
      } finally {
        // Clear loading state
        this.internalLoading = false;
      }
    },
    
    buildApiParams() {
      const params = {};
      
      // Add pagination parameters
      if (this.hasPagination) {
        params.page = this.currentPage;
        params.per_page = this.currentPerPage;
      }
      
      // Add search parameter
      if (this.hasSearch && this.currentSearchQuery) {
        params.search = this.currentSearchQuery;
      }
      
      // Add sort parameters
      if (this.currentSortColumn) {
        params.sort_by = this.currentSortColumn;
        params.sort_direction = this.currentSortDirection;
      }
      
      return params;
    },
    
    parseApiResponse(data) {
      // Check if response is in Laravel pagination format
      if (data.data && Array.isArray(data.data)) {
        // Laravel pagination format
        this.apiData = data.data;
        
        // Check if pagination metadata is in 'meta' object or at root level
        const meta = data.meta || data;
        
        // Extract pagination metadata
        this.apiPagination = {
          current: meta.current_page || 1,
          total: meta.last_page || 1,
          perPage: meta.per_page || this.currentPerPage,
          totalItems: meta.total || data.data.length
        };
        
        // Sync internal page with API response
        this.internalPage = this.apiPagination.current;
      } else if (Array.isArray(data)) {
        // Simple array format
        this.apiData = data;
        
        // No pagination metadata available
        this.apiPagination = null;
      } else {
        // Unknown format
        console.warn('ThemedTable: Unknown API response format', data);
        this.apiData = [];
        this.apiPagination = null;
      }
    },
    
    refresh() {
      // Expose refresh method for parent components
      if (this.isApiMode) {
        // Reload data from API
        return this.fetchApiData();
      } else {
        // In local mode, re-apply filters and sorting
        // This is handled automatically by computed properties
        // Just force a re-render
        this.$forceUpdate();
      }
    },
    
    handleReset() {
      // Reset all filters and sorting
      this.currentSearchQuery = '';
      this.currentSortColumn = null;
      this.currentSortDirection = 'asc';
      this.internalPage = 1;
      
      // In API mode, fetch fresh data
      if (this.isApiMode) {
        this.fetchApiData();
      }
      
      // Emit reset event
      this.$emit('reset');
    },
    
    handleRefresh() {
      // Refresh data without resetting filters
      this.refresh();
      
      // Emit refresh event
      this.$emit('refresh');
    },
    
    // Inline editing methods
    startEditRow(rowIndex) {
      // Don't allow starting edit in bulk mode (all rows are already editable)
      if (this.isBulkMode) {
        return;
      }
      
      // Don't allow starting edit in toggle mode if not in edit state
      if (this.isToggleMode && !this.externalEditMode) {
        return;
      }
      
      // Set editing row index
      this.editingRowIndex = rowIndex;
      
      // Get the row data
      const row = this.paginatedData[rowIndex];
      
      // Store original data for cancel
      this.originalRowData = { ...row };
      
      // Copy row data to editing form
      this.editingRowForm = { ...row };
    },
    
    startEditCell(rowIndex, field) {
      // Only for cell mode
      if (!this.isCellMode) {
        return;
      }
      
      // Set editing cell
      this.editingCell = { rowIndex, field };
      
      // Get the row data
      const row = this.paginatedData[rowIndex];
      
      // Store original data for cancel
      this.originalRowData = { ...row };
      
      // Initialize editing form with current row data
      this.editingRowForm = { ...row };
    },
    
    cancelEditRow(rowIndex) {
      // In toggle mode, don't allow canceling individual rows
      if (this.isToggleMode) {
        // Restore original data for this row
        const row = this.paginatedData[rowIndex];
        const rowId = this.getRowId(row);
        if (this.originalBulkData[rowId]) {
          Object.assign(row, this.originalBulkData[rowId]);
        }
        return;
      }
      
      // In bulk mode, restore original data for this row
      if (this.isBulkMode) {
        const row = this.paginatedData[rowIndex];
        const rowId = this.getRowId(row);
        if (this.originalBulkData[rowId]) {
          Object.assign(row, this.originalBulkData[rowId]);
          delete this.bulkEditForms[rowId];
        }
        return;
      }
      
      // Reset editing state for row/cell mode
      this.editingRowIndex = null;
      this.editingRowForm = {};
      this.originalRowData = {};
      this.savingRow = false;
    },
    
    cancelEditCell() {
      // Only for cell mode
      if (!this.isCellMode) {
        return;
      }
      
      // Reset editing state
      this.editingCell = null;
      this.editingRowForm = {};
      this.originalRowData = {};
    },
    
    async saveEditCell(rowIndex, field) {
      // Only for cell mode
      if (!this.isCellMode) {
        return;
      }
      
      // Get the row being edited
      const row = this.paginatedData[rowIndex];
      const rowId = this.getRowId(row);
      const newValue = this.editingRowForm[field];
      const oldValue = this.originalRowData[field];
      
      // If value hasn't changed, just cancel
      if (newValue === oldValue) {
        this.cancelEditCell();
        return;
      }
      
      // Validate the cell value
      const column = this.columns.find(c => c.field === field);
      if (column && column.validation) {
        const validationErrors = this.validateField(field, newValue);
        if (validationErrors.length > 0) {
          this.$emit('update-error', {
            row: this.editingRowForm,
            field,
            error: new Error(validationErrors.join(', ')),
            validationErrors
          });
          return;
        }
      }
      
      // If updateEndpoint is provided, make API request
      if (this.hasUpdateEndpoint) {
        this.savingRow = true;
        
        try {
          // Build update URL
          const url = this.buildUpdateUrl(rowId);
          
          // Build payload with only the changed field
          const payload = {
            [field]: newValue
          };
          
          // Make PATCH request (partial update)
          const response = await window.axios.patch(url, payload);
          
          // Update local data on success
          if (response.data) {
            const dataSource = this.isApiMode ? this.apiData : (this.data || this.internalData);
            const dataRowIndex = dataSource.findIndex(r => this.getRowId(r) === rowId);
            
            if (dataRowIndex !== -1) {
              this.$set(dataSource, dataRowIndex, response.data);
            }
          }
          
          // Emit update-success event
          this.$emit('update-success', {
            row: response.data || { ...row, [field]: newValue },
            originalRow: this.originalRowData,
            field,
            value: newValue
          });
          
          // Reset edit state
          this.cancelEditCell();
          
          // In API mode, refresh data
          if (this.isApiMode) {
            await this.fetchApiData();
          }
          
        } catch (error) {
          this.$emit('update-error', {
            row: this.editingRowForm,
            field,
            error
          });
          
          this.savingRow = false;
        }
      } else {
        // No updateEndpoint - emit update-success for parent to handle
        this.$emit('update-success', {
          row: { ...row, [field]: newValue },
          originalRow: this.originalRowData,
          field,
          value: newValue
        });
        
        // Update local data
        this.$set(row, field, newValue);
        
        // Reset edit state
        this.cancelEditCell();
      }
    },
    
    async saveEditRow(rowIndex) {
      // Get the row being edited
      const row = this.paginatedData[rowIndex];
      const rowId = this.getRowId(row);
      
      // Get the form data based on mode
      let formData;
      if (this.isBulkMode || this.isToggleMode) {
        formData = this.bulkEditForms[rowId] || { ...row };
      } else {
        formData = this.editingRowForm;
      }
      
      // Validate form data against column validation rules
      const validationErrors = this.validateEditForm(formData);
      if (validationErrors.length > 0) {
        // Display validation errors
        const errorMessage = validationErrors.join(', ');
        this.$emit('update-error', {
          row: formData,
          rowIndex,
          error: new Error(errorMessage),
          validationErrors
        });
        return;
      }
      
      // If updateEndpoint is provided, make API request
      if (this.hasUpdateEndpoint) {
        this.savingRow = true;
        
        try {
          // Build update URL
          const url = this.buildUpdateUrl(rowId);
          
          // Build complete payload with all row fields
          const payload = this.buildUpdatePayload(row, formData);
          
          // Make PUT/PATCH request
          const response = await window.axios.put(url, payload);
          
          // Update local data on success
          if (response.data) {
            // Find and update the row in display data
            const dataSource = this.isApiMode ? this.apiData : (this.data || this.internalData);
            const dataRowIndex = dataSource.findIndex(r => this.getRowId(r) === rowId);
            
            if (dataRowIndex !== -1) {
              // Update the row with response data
              this.$set(dataSource, dataRowIndex, response.data);
            }
          }
          
          // Emit update-success event
          this.$emit('update-success', {
            row: response.data || formData,
            originalRow: this.originalBulkData[rowId] || this.originalRowData,
            rowIndex
          });
          
          // Clean up based on mode
          if (this.isBulkMode || this.isToggleMode) {
            delete this.bulkEditForms[rowId];
            delete this.originalBulkData[rowId];
          } else {
            this.cancelEditRow(rowIndex);
          }
          
          this.savingRow = false;
          
          // In API mode, refresh data to get latest state from server
          if (this.isApiMode) {
            await this.fetchApiData();
          }
          
        } catch (error) {
          // Display error message and emit update-error event
          this.$emit('update-error', {
            row: formData,
            rowIndex,
            error
          });
          
          // Keep edit mode active on error so user can retry
          this.savingRow = false;
        }
      } else {
        // No updateEndpoint - emit update-success for parent to handle
        this.$emit('update-success', {
          row: formData,
          originalRow: this.originalBulkData[rowId] || this.originalRowData,
          rowIndex
        });
        
        // Update local data
        Object.assign(row, formData);
        
        // Clean up based on mode
        if (this.isBulkMode || this.isToggleMode) {
          delete this.bulkEditForms[rowId];
          delete this.originalBulkData[rowId];
        } else {
          this.cancelEditRow(rowIndex);
        }
      }
    },
    
    async saveAllRows() {
      // Only for bulk mode
      if (!this.isBulkMode) {
        return;
      }
      
      this.savingBulkEdit = true;
      const results = [];
      const errors = [];
      
      try {
        // Process each row that has been modified
        for (const [rowId, formData] of Object.entries(this.bulkEditForms)) {
          try {
            // Validate form data
            const validationErrors = this.validateEditForm(formData);
            if (validationErrors.length > 0) {
              errors.push({
                rowId,
                error: new Error(validationErrors.join(', ')),
                validationErrors
              });
              continue;
            }
            
            // Find the row
            const dataSource = this.isApiMode ? this.apiData : (this.data || this.internalData);
            const row = dataSource.find(r => this.getRowId(r) === rowId);
            
            if (!row) {
              continue;
            }
            
            if (this.hasUpdateEndpoint) {
              // Build update URL
              const url = this.buildUpdateUrl(rowId);
              
              // Build complete payload
              const payload = this.buildUpdatePayload(row, formData);
              
              // Make PUT/PATCH request
              const response = await window.axios.put(url, payload);
              
              // Update local data on success
              if (response.data) {
                const rowIndex = dataSource.findIndex(r => this.getRowId(r) === rowId);
                if (rowIndex !== -1) {
                  this.$set(dataSource, rowIndex, response.data);
                }
              }
              
              results.push({
                rowId,
                success: true,
                data: response.data
              });
            } else {
              // No API - just update local data
              Object.assign(row, formData);
              results.push({
                rowId,
                success: true,
                data: row
              });
            }
            
          } catch (error) {
            errors.push({
              rowId,
              error
            });
          }
        }
        
        // Emit events based on results
        if (errors.length === 0) {
          this.$emit('bulk-save-success', { results });
          
          // Clear bulk edit state
          this.bulkEditForms = {};
          this.originalBulkData = {};
          
          // Emit event to parent to toggle edit mode off
          this.$emit('edit-mode-complete');
          
          // In API mode, refresh data
          if (this.isApiMode) {
            await this.fetchApiData();
          }
        } else {
          this.$emit('bulk-save-error', { results, errors });
        }
        
      } finally {
        this.savingBulkEdit = false;
      }
    },
    
    cancelAllRows() {
      // Only for bulk mode
      if (!this.isBulkMode) {
        return;
      }
      
      // Restore all original data
      const dataSource = this.isApiMode ? this.apiData : (this.data || this.internalData);
      
      for (const [rowId, originalData] of Object.entries(this.originalBulkData)) {
        const rowIndex = dataSource.findIndex(r => this.getRowId(r) === rowId);
        if (rowIndex !== -1) {
          Object.assign(dataSource[rowIndex], originalData);
        }
      }
      
      // Clear bulk edit state
      this.bulkEditForms = {};
      this.originalBulkData = {};
      
      // Emit event to parent to toggle edit mode off
      this.$emit('edit-mode-cancelled');
    },
    
    validateField(field, value) {
      const errors = [];
      const column = this.columns.find(c => c.field === field);
      
      if (!column || !column.validation) {
        return errors;
      }
      
      const validation = column.validation;
      
      // Handle string validation (simple required check)
      if (typeof validation === 'string') {
        if (validation === 'required' && (value === null || value === undefined || value === '')) {
          errors.push(`${column.label || column.field} is required`);
        }
      }
      
      // Handle object validation (more complex rules)
      if (typeof validation === 'object') {
        if (validation.required && (value === null || value === undefined || value === '')) {
          errors.push(validation.message || `${column.label || column.field} is required`);
        }
        
        if (validation.minLength && String(value).length < validation.minLength) {
          errors.push(validation.message || `${column.label || column.field} minimum length is ${validation.minLength}`);
        }
        
        if (validation.maxLength && String(value).length > validation.maxLength) {
          errors.push(validation.message || `${column.label || column.field} maximum length is ${validation.maxLength}`);
        }
        
        if (validation.min !== undefined && Number(value) < validation.min) {
          errors.push(validation.message || `${column.label || column.field} minimum value is ${validation.min}`);
        }
        
        if (validation.max !== undefined && Number(value) > validation.max) {
          errors.push(validation.message || `${column.label || column.field} maximum value is ${validation.max}`);
        }
        
        if (validation.pattern) {
          const regex = new RegExp(validation.pattern);
          if (!regex.test(String(value))) {
            errors.push(validation.message || `${column.label || column.field} has invalid format`);
          }
        }
      }
      
      // Handle function validation (custom validator)
      if (typeof validation === 'function') {
        const result = validation(value, this.editingRowForm);
        
        if (result === false) {
          errors.push(`${column.label || column.field} validation failed`);
        }
        
        if (typeof result === 'string') {
          errors.push(result);
        }
      }
      
      return errors;
    },
    
    validateEditForm(formData) {
      const errors = [];
      
      // Validate each editable column
      for (const column of this.editableColumns) {
        const value = formData[column.field];
        const validation = column.validation;
        
        if (!validation) {
          continue;
        }
        
        // Handle string validation (simple required check)
        if (typeof validation === 'string') {
          if (validation === 'required' && (value === null || value === undefined || value === '')) {
            errors.push(`${column.label || column.field} is required`);
          }
        }
        
        // Handle object validation (more complex rules)
        if (typeof validation === 'object') {
          if (validation.required && (value === null || value === undefined || value === '')) {
            errors.push(validation.message || `${column.label || column.field} is required`);
          }
          
          if (validation.minLength && String(value).length < validation.minLength) {
            errors.push(validation.message || `${column.label || column.field} minimum length is ${validation.minLength}`);
          }
          
          if (validation.maxLength && String(value).length > validation.maxLength) {
            errors.push(validation.message || `${column.label || column.field} maximum length is ${validation.maxLength}`);
          }
          
          if (validation.min !== undefined && Number(value) < validation.min) {
            errors.push(validation.message || `${column.label || column.field} minimum value is ${validation.min}`);
          }
          
          if (validation.max !== undefined && Number(value) > validation.max) {
            errors.push(validation.message || `${column.label || column.field} maximum value is ${validation.max}`);
          }
          
          if (validation.pattern) {
            const regex = new RegExp(validation.pattern);
            if (!regex.test(String(value))) {
              errors.push(validation.message || `${column.label || column.field} has invalid format`);
            }
          }
        }
        
        // Handle function validation (custom validator)
        if (typeof validation === 'function') {
          const result = validation(value, this.editingRowForm);
          
          if (result === false) {
            errors.push(`${column.label || column.field} validation failed`);
          }
          
          if (typeof result === 'string') {
            errors.push(result);
          }
        }
      }
      
      return errors;
    },
    
    // Helper methods
    getRowId(row) {
      // Get row identifier using rowIdField prop
      return row[this.rowIdField];
    },
    
    buildUpdateUrl(rowId) {
      // Replace :id in updateEndpoint with actual row ID
      return this.updateEndpoint.replace(':id', rowId);
    },
    
    buildUpdatePayload(row, updates) {
      // Merge row data with updates
      // Include all required fields for backend validation
      return { ...row, ...updates };
    },
    
    async processBulkUpdateEndpoint(action) {
      // Process bulk update using dedicated bulk endpoint
      this.processingBulkAction = true;
      
      try {
        // Prepare items for bulk update
        const items = this.selectedRows.map(row => ({
          ...row,
          [action.field]: action.value
        }));
        
        // Make single bulk update request using configured payload key
        const response = await window.axios.put(this.bulkUpdateEndpoint, {
          [this.bulkUpdatePayloadKey]: items
        });
        
        // Clear selection before refresh to prevent UI issues
        const selectedCopy = [...this.selectedRows];
        this.selectedRows = [];
        
        // Refresh data to show updates
        if (this.isApiMode) {
          await this.fetchApiData();
        }
        
        // Emit success event
        this.$emit('bulk-action-complete', {
          action: action.name,
          selected: selectedCopy,
          results: response.data
        });
        
      } catch (error) {
        console.error('Bulk update error:', error);
        
        // Emit error event
        this.$emit('bulk-action-error', {
          action: action.name,
          selected: this.selectedRows,
          errors: [error]
        });
      } finally {
        this.processingBulkAction = false;
      }
    },
    
    updateEditValue(field, value) {
      // Update the editing form value for a specific field
      if (this.isBulkMode || this.isToggleMode) {
        // In bulk/toggle mode, update the bulk edit form for the current row
        const rowIndex = this.editingCell?.rowIndex || this.editingRowIndex;
        if (rowIndex !== null && rowIndex !== undefined) {
          const row = this.paginatedData[rowIndex];
          const rowId = this.getRowId(row);
          
          if (!this.bulkEditForms[rowId]) {
            this.$set(this.bulkEditForms, rowId, { ...row });
            this.$set(this.originalBulkData, rowId, { ...row });
          }
          
          this.$set(this.bulkEditForms[rowId], field, value);
          
          // Emit event so parent components can track changes
          this.$emit('update-edit-value', field, value, rowIndex, row);
        }
      } else {
        // In row/cell mode, update the editing form
        this.$set(this.editingRowForm, field, value);
        
        // Emit event so parent components can track changes
        this.$emit('update-edit-value', field, value);
      }
    },
    
    getEditingState(rowIndex, field) {
      // Determine if this cell is in editing state
      if (this.isCellMode) {
        return this.editingCell && 
               this.editingCell.rowIndex === rowIndex && 
               this.editingCell.field === field;
      }
      
      // For other modes, check if row is editing
      if (this.isBulkMode) {
        return this.externalEditMode;
      }
      if (this.isToggleMode) {
        return this.externalEditMode;
      }
      // Row mode
      return this.editingRowIndex === rowIndex;
    },
    
    getEditValue(rowIndex, field) {
      // Get the edit value for a cell
      const row = this.paginatedData[rowIndex];
      const rowId = this.getRowId(row);
      
      if (this.isBulkMode || this.isToggleMode) {
        // Return value from bulk edit form or original row value
        return this.bulkEditForms[rowId]?.[field] ?? this.getCellValue(row, field);
      } else if (this.isCellMode && this.editingCell?.rowIndex === rowIndex && this.editingCell?.field === field) {
        // Return value from editing form for this specific cell
        return this.editingRowForm[field];
      } else if (this.isRowMode && this.editingRowIndex === rowIndex) {
        // Return value from editing form for this row
        return this.editingRowForm[field];
      }
      
      return this.getCellValue(row, field);
    },
    
    getSavingState(rowIndex) {
      // Check if this row is currently being saved
      if (this.isBulkMode) {
        return this.savingBulkEdit;
      }
      
      // Check if this specific row is being saved
      if (this.editingRowIndex === rowIndex) {
        return this.savingRow;
      }
      
      if (this.editingCell && this.editingCell.rowIndex === rowIndex) {
        return this.savingRow;
      }
      
      return false;
    },
    
    initializeBulkEdit() {
      // Initialize bulk edit mode - prepare all rows for editing
      if (!this.isBulkMode || !this.externalEditMode) {
        return;
      }
      
      const dataSource = this.paginatedData;
      
      for (const row of dataSource) {
        const rowId = this.getRowId(row);
        if (!this.bulkEditForms[rowId]) {
          this.$set(this.bulkEditForms, rowId, { ...row });
          this.$set(this.originalBulkData, rowId, { ...row });
        }
      }
    },
    
    cleanupBulkEdit() {
      // Clean up bulk edit state when exiting bulk mode
      this.bulkEditForms = {};
      this.originalBulkData = {};
      this.savingBulkEdit = false;
    },
    
    // Row details methods
    showRowDetailsModal(row) {
      this.selectedRowForDetails = row;
      this.$modal.show('row-details-modal');
    },
    
    hideRowDetailsModal() {
      this.$modal.hide('row-details-modal');
    },
    
    handleModalClosed() {
      // Clear selected row when modal is closed
      this.selectedRowForDetails = null;
    },
    
    handleActionFromPopup(event) {
      // Forward the action event from RowDetailsPopup
      this.$emit('action', event);
    },
    
    // Column resizing methods
    handleResizeStart({ column, startX, startWidth }) {
      this.resizingColumn = column.field;
      this.resizeStartX = startX;
      this.resizeStartWidth = startWidth;
      
      // Add resizing class to body to change cursor globally
      document.body.classList.add('column-resizing');
    },
    
    handleResizeMove(event) {
      if (!this.resizingColumn) {
        return;
      }
      
      // Calculate new width
      const deltaX = event.clientX - this.resizeStartX;
      const newWidth = Math.max(50, this.resizeStartWidth + deltaX); // Minimum 50px
      
      // Update column width
      this.$set(this.columnWidths, this.resizingColumn, newWidth);
    },
    
    handleResizeEnd() {
      if (!this.resizingColumn) {
        return;
      }
      
      // Clear resizing state
      this.resizingColumn = null;
      this.resizeStartX = 0;
      this.resizeStartWidth = 0;
      
      // Remove resizing class from body
      document.body.classList.remove('column-resizing');
      
      // Emit resize event for parent to save preferences
      this.$emit('column-resize', {
        widths: { ...this.columnWidths }
      });
    },
    
    handleColumnToggle({ field, visible }) {
      // Update internal visible columns list
      if (visible) {
        // Add column to visible list if not already present
        if (!this.internalVisibleColumns.includes(field)) {
          this.internalVisibleColumns.push(field);
        }
      } else {
        // Remove column from visible list
        this.internalVisibleColumns = this.internalVisibleColumns.filter(f => f !== field);
      }
      
      // Emit event for parent to save preferences
      this.$emit('column-visibility-change', {
        visibleColumns: [...this.internalVisibleColumns]
      });
    },
    
    initializeVisibleColumns() {
      // Initialize visible columns on mount
      if (this.columnVisibility && this.columns) {
        this.internalVisibleColumns = [...this.currentVisibleColumns];
      }
    }
  },
  
  mounted() {
    // Initialize visible columns
    this.initializeVisibleColumns();
    
    // Fetch data on mount if in API mode
    if (this.isApiMode) {
      this.fetchApiData();
    }
    
    // Add global mouse event listeners for column resizing
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  },
  
  beforeDestroy() {
    // Clean up global event listeners
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  },
  
  watch: {
    apiEndpoint() {
      // Refetch data when API endpoint changes
      if (this.isApiMode) {
        this.fetchApiData();
      }
    },
    externalEditMode(newVal) {
      // Handle external edit mode changes
      if (this.isBulkMode && newVal) {
        // Entering bulk edit mode
        this.initializeBulkEdit();
      } else if (this.isBulkMode && !newVal) {
        // Exiting bulk edit mode
        this.cleanupBulkEdit();
      } else if (this.isToggleMode && newVal) {
        // Entering toggle edit mode - prepare all rows
        this.initializeBulkEdit();
      } else if (this.isToggleMode && !newVal) {
        // Exiting toggle edit mode
        this.cleanupBulkEdit();
      }
    }
  }
}
</script>

<style scoped>
/* Component-specific styles are in table.less */
/* This scoped style section ensures proper encapsulation */
</style>
